import {
  BuildingOfficeIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  CubeIcon,
  LinkIcon,
  MapPinIcon,
  TagIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

/** A feature that has to be on for a page to show. Matches the flag names used in AdminMenu. */
export type AdminPageGate = "Triage" | "DEFRA" | "PartPro Trade" | "Till" | "Part History" | "Call Centre";

export type AdminGroupKey =
  | "business"
  | "yard"
  | "parts"
  | "selling"
  | "shipping"
  | "compliance"
  | "integrations"
  | "settings";

export interface IAdminPage {
  /** The `:section` segment under /admin. */
  href: string;
  label: string;
  description: string;
  /** Extra words the search should match that aren't in the label or description. */
  keywords?: string[];
  gate?: AdminPageGate;
}

export interface IAdminGroup {
  key: AdminGroupKey;
  name: string;
  description: string;
  icon: typeof CubeIcon;
  pages: IAdminPage[];
}

/**
 * Every admin page, grouped the way the landing page shows them. Order within a group is the
 * order the pills appear in, so put the everyday ones first. The customer's own Admin Settings
 * prefixes (FedEx:, Invoice:, …) become the pills on the Settings card, added by useAdminSections.
 */
export const ADMIN_GROUPS: IAdminGroup[] = [
  {
    key: "business",
    name: "Business",
    description: "Currency, invoicing, pricing and who can sign in",
    icon: BuildingOfficeIcon,
    pages: [
      { href: "staff", label: "Staff", description: "View and manage your staff", keywords: ["users", "sign in", "login", "people", "accounts"] },
      { href: "roles", label: "Roles", description: "Create roles, control feature access, and assign staff", keywords: ["permissions", "access"] },
      { href: "sites", label: "Sites", description: "Depots and which yard and warehouse locations belong to each", keywords: ["depot", "branch"] },
      { href: "vat-margin-scheme", label: "VAT / margin scheme", description: "UK Global Margin Scheme and the standard VAT rate", keywords: ["tax", "global accounting"] },
      { href: "stock-number-format", label: "Stock numbers", description: "How vehicle stock numbers are generated using merge tags", keywords: ["format", "numbering"] },
      { href: "warranty-manager", label: "Warranties", description: "Warranty information for parts", keywords: ["guarantee", "returns"] },
      { href: "till", label: "Till", description: "Defaults, receipts and card readers for the till", keywords: ["pos", "point of sale", "card reader", "receipt"], gate: "Till" },
      { href: "call-centre", label: "Call Centre", description: "Calling hours, pricing, targets and who is served what in the live queue", keywords: ["quotes", "live queue", "agents", "max offer", "call back"], gate: "Call Centre" },
    ],
  },
  {
    key: "yard",
    name: "Yard",
    description: "How vehicles move from check-in to end of life",
    icon: MapPinIcon,
    pages: [
      { href: "check-in-editor", label: "Check-in", description: "The steps a vehicle goes through on arrival", keywords: ["arrival", "inspection", "checkin"] },
      { href: "daily-checks-editor", label: "Daily checks", description: "Daily checks and their steps", keywords: ["inspection", "forklift"] },
      { href: "daily-checks", label: "Daily check results", description: "Daily check results for a given day", keywords: ["history", "report"] },
      { href: "depollution-editor", label: "Depollution", description: "The steps for depolluting a vehicle", keywords: ["drain", "fluids", "decontamination"] },
      { href: "triage-editor", label: "Triage", description: "The steps for triaging a vehicle", gate: "Triage" },
      { href: "dismantling-bay-editor", label: "Dismantling bays", description: "Dismantling bays and production lines", keywords: ["production", "bay"] },
      { href: "locations", label: "Yard locations", description: "The locations vehicles are stored in", keywords: ["spaces", "slots", "parking"] },
      { href: "location-groups", label: "Yard sections", description: "Yard sections and their availability", keywords: ["areas", "zones", "groups"] },
      { href: "trucks", label: "Trucks", description: "The trucks that collect vehicles", keywords: ["lorry", "collection", "driver", "trips", "vehicles"] },
    ],
  },
  {
    key: "parts",
    name: "Parts",
    description: "How parts are named, stored, photographed and tracked",
    icon: CubeIcon,
    pages: [
      { href: "part-image-settings", label: "Images", description: "Watermarks and sizing for part photos", keywords: ["photos", "watermark", "pictures"] },
      { href: "part-tags", label: "Tags", description: "Which fields print on part tags, per category", keywords: ["labels", "print", "barcode"] },
      { href: "part-assembly-editor", label: "Assemblies", description: "Which parts make up an assembly", keywords: ["kits", "sub assembly"] },
      { href: "part-category-sub-parts", label: "Category sub-parts", description: "Which categories are included within a main category", keywords: ["sub parts"] },
      { href: "part-bins", label: "Bins", description: "The bins parts get picked into", keywords: ["picking", "totes"] },
      { href: "warehouse-locations", label: "Warehouse locations", description: "Warehouse shelving locations", keywords: ["shelf", "racking", "storage"] },
      { href: "materials", label: "Materials", description: "The materials parts and vehicles are weighed in as", keywords: ["scrap", "metal", "weights"] },
      { href: "part-history-fields", label: "History fields", description: "Which changes appear in a part's history", keywords: ["audit", "changes"], gate: "Part History" },
    ],
  },
  {
    key: "selling",
    name: "Selling",
    description: "Marketplaces and the rules for listing on them",
    icon: TagIcon,
    pages: [
      { href: "listing-rules", label: "Listing rules", description: "Rules for categories to automatically list to eBay", keywords: ["auto list", "automatic"] },
      { href: "listing-restrictions", label: "Listing restrictions", description: "Keep parts a marketplace won't accept, such as airbags on eBay, from being listed there", keywords: ["banned", "blocked", "airbags"] },
      { href: "template-editor", label: "Templates", description: "The description templates listings are built from", keywords: ["html", "description", "design"] },
      { href: "template-policies", label: "Template policies", description: "Which template and policies each category lists with", keywords: ["defaults", "categories"] },
      { href: "end-slate-editor", label: "End slates", description: "Images added to the end of every eBay listing", keywords: ["banner", "footer images"] },
      { href: "replace-ebay-policy", label: "Replace eBay policy", description: "Move every unsold part from one eBay policy to another", keywords: ["bulk", "fulfilment", "payment", "returns"] },
      { href: "partpro-trade", label: "PartPro Trade", description: "Which bid categories you offer on PartPro's trade portal", keywords: ["bids", "portal"], gate: "PartPro Trade" },
    ],
  },
  {
    key: "shipping",
    name: "Shipping",
    description: "Couriers, boxes and manifests",
    icon: TruckIcon,
    pages: [
      { href: "shipping-providers", label: "Couriers", description: "Shipping providers and their account details", keywords: ["fedex", "dpd", "proovia", "carrier", "providers"] },
      { href: "box-size-editor", label: "Box sizes", description: "The boxes parts are packed in", keywords: ["packaging", "dimensions", "parcel"] },
      { href: "shipping-policies", label: "Shipping policies", description: "The shipping policies listings are sold with", keywords: ["postage", "delivery", "fulfilment"] },
      { href: "shopify-shipping-policy-sync", label: "Shopify shipping sync", description: "Map eBay fulfilment policies to Shopify delivery profiles", keywords: ["delivery profiles"] },
    ],
  },
  {
    key: "compliance",
    name: "Compliance",
    description: "Regulatory paperwork and reporting",
    icon: ClipboardDocumentCheckIcon,
    pages: [
      { href: "cod", label: "COD", description: "Certificate of Destruction credentials and signature", keywords: ["certificate of destruction", "dvla", "signature"] },
      { href: "waste-tracking", label: "Waste tracking", description: "DEFRA receiver details used on every waste movement", keywords: ["defra", "consignment note", "hazardous"], gate: "DEFRA" },
    ],
  },
  {
    key: "integrations",
    name: "Integrations",
    description: "Lead feeds and other connected services",
    icon: LinkIcon,
    pages: [
      { href: "lead-import", label: "Lead import", description: "Check why a partner lead did not come through, and re-run the import", keywords: ["email", "mailbox", "partner", "importer"] },
      { href: "connections", label: "Auth connections", description: "Sign-ins to eBay, Shopify, PartPro and other sites", keywords: ["oauth", "connect", "authorise", "ebay", "shopify", "partpro"] },
      { href: "sending-domain", label: "Sending domain", description: "Verify your own domain so outbound mail can be sent from your addresses", keywords: ["email", "dns", "spf", "dkim"] },
    ],
  },
  {
    // Deliberately last: the raw key/value settings, one pill per prefix the customer has
    key: "settings",
    name: "Settings",
    description: "Keys, credentials and other raw settings, grouped by prefix",
    icon: Cog6ToothIcon,
    pages: [],
  },
];

/** The group whose card lists the Admin Settings prefixes. */
export const SETTINGS_GROUP_KEY: AdminGroupKey = "settings";

